interface TreeNode {
  value: number;
  children?: [TreeNode, TreeNode];
}

interface Props {
  tree: TreeNode;
  finalAnswer?: string; // optional display like "2² × 3 × 5"
  title?: string;
}

// Factor tree — matches exactly how Big Ideas Math teaches prime factorization
// in section 1.3. Composite numbers (coral) split into factor pairs until
// every leaf is prime (sky).
export default function FactorTreeViz({
  tree,
  finalAnswer,
  title = 'Factor tree',
}: Props) {
  // Compute tree dimensions for layout. Each leaf takes ~60px, spacing doubles up.
  const depth = getDepth(tree);
  const leafCount = getLeafCount(tree);
  const nodeWidth = 52;
  const levelHeight = 56;
  const width = Math.max(280, leafCount * nodeWidth);
  const height = depth * levelHeight + 20;

  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-display text-lg">
        {title}{' '}
        <span className="text-ink/50 text-sm font-body font-normal math-display">
          for {tree.value}
        </span>
      </h3>
      <div className="flex justify-center overflow-x-auto">
        <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ maxWidth: width, height: 'auto' }}>
          <RenderNode node={tree} x={width / 2} y={20} xSpan={width * 0.4} levelHeight={levelHeight} />
        </svg>
      </div>
      {finalAnswer && (
        <div className="bg-cream rounded-xl px-4 py-3 text-center math-display font-bold text-base">
          {tree.value} = {finalAnswer}
        </div>
      )}
    </div>
  );
}

function RenderNode({
  node,
  x,
  y,
  xSpan,
  levelHeight,
}: {
  node: TreeNode;
  x: number;
  y: number;
  xSpan: number;
  levelHeight: number;
}) {
  const isPrime = !node.children;
  const fill = isPrime ? '#2b8bd8' : '#ff7a6b';
  const r = 18;

  const childY = y + levelHeight;
  const leftX = x - xSpan / 2;
  const rightX = x + xSpan / 2;

  return (
    <g>
      {/* Draw lines to children first so they sit behind the nodes */}
      {node.children && (
        <>
          <line x1={x} y1={y + r} x2={leftX} y2={childY - r} stroke="#c9d2de" strokeWidth="1.5" />
          <line x1={x} y1={y + r} x2={rightX} y2={childY - r} stroke="#c9d2de" strokeWidth="1.5" />
          <RenderNode
            node={node.children[0]}
            x={leftX}
            y={childY}
            xSpan={xSpan / 2}
            levelHeight={levelHeight}
          />
          <RenderNode
            node={node.children[1]}
            x={rightX}
            y={childY}
            xSpan={xSpan / 2}
            levelHeight={levelHeight}
          />
        </>
      )}

      {/* The node itself */}
      <circle cx={x} cy={y} r={r} fill={fill} />
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="13"
        fontWeight="700"
        fill="#ffffff"
        fontFamily="JetBrains Mono, monospace"
      >
        {node.value}
      </text>
    </g>
  );
}

function getDepth(node: TreeNode): number {
  if (!node.children) return 1;
  return 1 + Math.max(getDepth(node.children[0]), getDepth(node.children[1]));
}

function getLeafCount(node: TreeNode): number {
  if (!node.children) return 1;
  return getLeafCount(node.children[0]) + getLeafCount(node.children[1]);
}
